import React, { useId } from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from '@/app/lib/utils';

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'default';
};

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, size = 'default', children, ...props }, ref) => {
    const randomId = useId();
    const id = props.id || randomId;
    const sizeVariantClassName = {
      xs: 'h-3 w-6',
      sm: 'h-4 w-8',
      default: 'h-[20px] w-[32px]',
      md: 'h-5 w-9',
      lg: 'h-6 w-11',
    };

    const tumbSize = {
      xs: 'h-2 w-2 data-[state=checked]:translate-x-3',
      sm: 'h-3 w-3 data-[state=checked]:translate-x-4',
      default: 'h-4 w-4 data-[state=checked]:translate-x-[13px]',
      md: 'h-4 w-4 data-[state=checked]:translate-x-4',
      lg: 'h-5 w-5 data-[state=checked]:translate-x-5',
    };

    const switchElement = (
      <SwitchPrimitives.Root
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
          sizeVariantClassName[size],
          className
        )}
        {...props}
        id={id}
        ref={ref}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none block rounded-full bg-background shadow-sm ring-0 transition-transform data-[state=unchecked]:translate-x-0',
            tumbSize[size]
          )}
        />
      </SwitchPrimitives.Root>
    );

    if (!children) return switchElement;

    return (
      <div className="flex items-center gap-2">
        {switchElement}
        <label className="cursor-pointer text-sm text-foreground" htmlFor={id}>
          {children}
        </label>
      </div>
    );
  }
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
export default Switch;
